import React, {useCallback, useState} from 'react';
import {Box, useTheme} from "@mui/material";
import {GridColDef, GridRenderCellParams} from "@mui/x-data-grid";
import {useQueryClient} from "@tanstack/react-query";
import {
  CustomFooter,
  DefaultGrid,
  GridHeadingWithExport,
} from "../../../../components/common/Tables/DefaultGrid";
import {Pill} from "../../../../components/common/PIll";
import {useErrorDialog} from "../../../../components/common/Dialog/ErrorDialog";
import {ICompany} from "../../../../types/Company";
import {useMutationWithAccessToken} from "../../../../hooks/TanstackHooks";
import {
  cacheKeys,
  isSaasCatalogueQueryKey,
  isSaasStatusDependentQueryKey,
} from "../../../../services/shared/serviceConstants";
import {
  ISaasClientRestriction,
  setSaasClientRestriction,
  setSaasDisabled,
} from "../../../../services/SaasRestrictions/SaasRestrictionsQueries";
import {renderIntegrationEllipsis} from "./IntegrationsActionMenu";
import {EditIntegrationAvailabilityDialog} from "./EditIntegrationAvailabilityDialog";
import {SetIntegrationStatusDialog} from "./SetIntegrationStatusDialog";

export const allClientsLabel = 'All clients';

// "All clients", or the names of the clients a custom integration is restricted to
export const availableToText = (
  integration: ISaasClientRestriction,
  clients: ICompany[],
) => integration.restricted ?
  integration.clientIds
    .map((id) => clients.find((c) => c.id === id)?.name ?? id)
    .sort((a, b) => a.localeCompare(b))
    .join(', ') :
  allClientsLabel;

export const IntegrationsComponent = ({
  userRole,
  integrations,
  clients,
}: {
  userRole: string,
  integrations: ISaasClientRestriction[],
  clients: ICompany[],
}) => {
  const theme = useTheme();
  const minRowsPerPageOption = 100
  const rowsPerPageOptions = [minRowsPerPageOption]

  const queryClient = useQueryClient();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUpdatePending, setIsUpdatePending] = useState(false);
  const [currentIntegration, setCurrentIntegration] = useState<ISaasClientRestriction>();
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isStatusUpdatePending, setIsStatusUpdatePending] = useState(false);

  // Construct the error dialog.
  const {
    dialog: errorDialog,
    setIsOpen: setIsErrorDialogOpen,
    setErrorMessage,
  } = useErrorDialog({
    onDismiss: ()=>{},
  })

  const {
    mutate: doSetRestriction,
  } = useMutationWithAccessToken({
    mutation: setSaasClientRestriction,
    tanstackOptions: {
      onSuccess: () => {
        // Re-query this list and every client's catalogue of SaaSes to add
        queryClient.invalidateQueries({
          predicate: ({queryKey}) => {
            const [first] = queryKey;
            return (typeof first === 'string' && first.startsWith(cacheKeys.saasClientRestrictions)) ||
              isSaasCatalogueQueryKey(queryKey);
          }
        });
        setIsDialogOpen(false);
        setIsUpdatePending(false);
        setCurrentIntegration(undefined);
      },
      onError: (error: any) => {
        setIsErrorDialogOpen(true);
        if (error.response && error.response.errors && error.response.errors.length > 0) {
          setErrorMessage(error.response.errors[0].message);
        }
        else {
          setErrorMessage("Could not update integration");
        }
        setIsDialogOpen(false);
        setIsUpdatePending(false);
      }
    }
  });

  const showMutationError = (
    error: { response?: { errors?: { message: string }[] } },
    fallbackMessage: string,
  ) => {
    setIsErrorDialogOpen(true);
    setErrorMessage(error.response?.errors?.[0]?.message ?? fallbackMessage);
  }

  const {
    mutate: doSetDisabled,
  } = useMutationWithAccessToken({
    mutation: setSaasDisabled,
    tanstackOptions: {
      onSuccess: () => {
        // Re-query this list (the status column), every client's catalogue
        // of SaaSes to add and the connected SaaSes, which hide disabled ones
        queryClient.invalidateQueries({
          predicate: ({queryKey}) => isSaasStatusDependentQueryKey(queryKey)
        });
        setIsStatusDialogOpen(false);
        setIsStatusUpdatePending(false);
        setCurrentIntegration(undefined);
      },
      onError: (error: { response?: { errors?: { message: string }[] } }) => {
        showMutationError(error, "Could not update integration status");
        setIsStatusDialogOpen(false);
        setIsStatusUpdatePending(false);
        setCurrentIntegration(undefined);
      }
    }
  });

  const onSetStatusMenuClick = useCallback((integration: ISaasClientRestriction) => {
    setCurrentIntegration(integration);
    setIsStatusDialogOpen(true);
  }, []);

  const onCancelSetStatus = () => {
    setIsStatusUpdatePending(false);
    setIsStatusDialogOpen(false);
    setCurrentIntegration(undefined);
  }

  const onConfirmSetStatus = (status: {saas: string, isDisabled: boolean}) => {
    setIsStatusUpdatePending(true);
    doSetDisabled({...status, role: userRole});
  }

  const onUpdateMenuClick = useCallback((integration: ISaasClientRestriction) => {
    setCurrentIntegration(integration);
    setIsDialogOpen(true);
  }, []);

  const onCancelUpdate = () => {
    setIsUpdatePending(false);
    setIsDialogOpen(false);
    setCurrentIntegration(undefined);
  }

  const onConfirmUpdate = (restriction: {saas: string, restricted: boolean, clientIds: string[]}) => {
    setIsUpdatePending(true);
    doSetRestriction({...restriction, role: userRole});
  }

  const columns = useCallback(
    (): GridColDef [] => {
      return [
        {
          headerName: 'Name',
          field: 'name',
          minWidth: 200,
          flex: 2,
          sortable: true,
        },
        {
          headerName: 'Identifier',
          field: 'identifier',
          flex: 2,
          sortable: true,
        },
        {
          headerName: 'Status',
          field: 'isDisabled',
          flex: 1,
          sortable: true,
          valueGetter: (value: boolean) => value ? 'Disabled' : 'Enabled',
        },
        {
          headerName: 'Available to',
          field: 'availableTo',
          flex: 4,
          sortable: true,
          valueGetter: (_value: unknown, row: ISaasClientRestriction) =>
            availableToText(row, clients),
          renderCell: (params: GridRenderCellParams) => {
            const integration = params.row as ISaasClientRestriction;
            return integration.restricted ?
              <Box sx={{display: 'flex', alignItems: 'center', gap: theme.spacing(1), height: '100%'}}>
                <Pill
                  pillWidth="70px"
                  pillColor={theme.more.sjsOrange}
                  title="Custom"
                  hoverText="Custom integration, only available to the listed clients"
                />
                <Box
                  component="span"
                  sx={{overflow: 'hidden', textOverflow: 'ellipsis'}}
                  title={params.value}
                >
                  {params.value}
                </Box>
              </Box> :
              params.value;
          },
        },
        {
          headerName: '',
          field: 'action',
          renderCell: (params: GridRenderCellParams) =>
            renderIntegrationEllipsis({
              integration: params.row,
              onUpdateClick: onUpdateMenuClick,
              onSetStatusClick: onSetStatusMenuClick,
            }),
          sortable: false,
          disableExport: true,
          disableReorder: true,
          filterable: false,
          disableColumnMenu: true,
          flex: 1,
        },
      ];
    },
    [clients, onUpdateMenuClick, onSetStatusMenuClick, theme]
  );

  const [paginationModel, setPaginationModel] = useState({
    pageSize: minRowsPerPageOption,
    page: 0,
  });

  return (<>
      {isDialogOpen && currentIntegration && <EditIntegrationAvailabilityDialog
        integration={currentIntegration}
        clients={clients}
        isOpen={isDialogOpen}
        isSpinning={isUpdatePending}
        onCancel={onCancelUpdate}
        onConfirm={onConfirmUpdate}
      />}
      {isStatusDialogOpen && currentIntegration && <SetIntegrationStatusDialog
        integration={currentIntegration}
        isOpen={isStatusDialogOpen}
        isSpinning={isStatusUpdatePending}
        onCancel={onCancelSetStatus}
        onConfirm={onConfirmSetStatus}
      />}
      {errorDialog}
      <DefaultGrid
        rows={integrations}
        getRowId={row => row.identifier}
        columns={columns()}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={rowsPerPageOptions}
        length={integrations?.length}
        minRowsPerPageOption={minRowsPerPageOption}
        initialState={{
          sorting: {
            sortModel: [{
              field: "name",
              sort: "asc",
            }],
          },
        }}
        toolbar={() =>
          <GridHeadingWithExport
            heading="Integrations"
            shouldShowExportButton={false}
            shouldShowBackToPrevious={false}
            tableDescription={<>
              Restrict an integration to selected clients to make it a custom integration
              that only they can add. Disable an integration to hide it from every client.
            </>}
          />}
        footer={() => CustomFooter({})}
      />
    </>
  )
};
